import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '@crave/api-client';
import { Button } from '@crave/ui';
import { api } from '../api.ts';
import { Notice, statusWords } from '../components/ui.tsx';
import { useInboxLiveState } from '../lib/inboxLive.ts';

interface ConversationItem {
  id: string;
  phone: string;
  phone_display: string;
  customer: { id: string; name: string | null } | null;
  opted_out: boolean;
  unread: number;
  last_message_at: string;
  last_message: { body: string; direction: string; source: string } | null;
}
interface Message {
  id: string;
  direction: 'inbound' | 'outbound';
  source: 'customer' | 'staff' | 'automatic';
  body: string;
  media: string[];
  status: string;
  error: string | null;
  created_at: string;
  sent_by_name: string | null;
  order_number: string | null;
}
interface Conversation {
  id: string;
  phone: string;
  phone_display: string;
  opted_out: boolean;
  opted_out_at: string | null;
  customer: { id: string; name: string | null; email: string | null } | null;
  latest_order: { id: string; order_number: string; status: string; pickup_label: string } | null;
  messages: Message[];
}
interface SendResult {
  conversationId: string;
  status: 'sent' | 'failed';
  error: string | null;
}

/**
 * Customer texts to and from the Crave number, laid out like Messages on an iPhone or Mac: the
 * conversation list on the left, the open conversation on the right. On a phone they are two
 * screens. Order texts sent automatically show in the thread too, marked as automatic.
 */
export function Messages() {
  const { id } = useParams();
  const [composing, setComposing] = useState(false);
  const open = id ?? (composing ? 'new' : null);
  return (
    <section className="msg-shell" data-open={open ? 'thread' : 'list'}>
      <style>{MESSAGES_CSS}</style>
      <ConversationList selected={id ?? null} onNew={() => setComposing(true)} />
      <div className="msg-pane">
        {id ? (
          <Thread key={id} id={id} />
        ) : composing ? (
          <NewText onClose={() => setComposing(false)} />
        ) : (
          <div className="msg-empty">
            <strong>Messages</strong>
            <p>Texts to (863) 777-4887 show up here. Pick a conversation to read and reply.</p>
          </div>
        )}
      </div>
    </section>
  );
}

const MESSAGES_CSS = `
.msg-shell { display: grid; grid-template-columns: minmax(260px, 340px) 1fr; height: calc(100vh - 64px);
  min-height: 520px; background: var(--paper); border-radius: 16px; overflow: hidden;
  box-shadow: 0 0 0 1px var(--line); }
.msg-list { display: flex; flex-direction: column; min-height: 0; border-right: 1px solid var(--line); background: #fbfaf7; }
.msg-list-head { display: grid; gap: 10px; padding: 14px 14px 10px; }
.msg-list-title { display: flex; justify-content: space-between; align-items: center; }
.msg-list-title h2 { margin: 0; font-size: 24px; margin-right: auto; }
.msg-live { font-size: 12px; font-weight: 600; color: var(--ink-soft); display: inline-flex; align-items: center; gap: 6px; margin-right: 4px; }
.msg-live[data-state='live']::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: #2fb65d;
  box-shadow: 0 0 0 3px rgba(47,182,93,.18); }
.msg-live[data-state='connecting']::before, .msg-live[data-state='retrying']::before { content: ''; width: 8px; height: 8px;
  border-radius: 50%; background: #d9a441; }
.msg-bubble[data-pending] { opacity: .6; }
.msg-search { width: 100%; min-height: 40px; border: 0; border-radius: 10px; padding: 0 12px;
  background: #efece6; font-size: 15px; color: var(--ink); }
.msg-search:focus-visible { outline: 2px solid var(--crave-red); }
.msg-rows { overflow-y: auto; flex: 1; padding: 0 8px 8px; }
.msg-row { display: grid; grid-template-columns: 44px 1fr; gap: 10px; align-items: center; padding: 10px;
  border-radius: 12px; color: inherit; text-decoration: none; min-height: 64px; }
.msg-row:hover { background: #f1eee8; }
.msg-row[aria-current='page'] { background: var(--crave-red); color: #fff; }
.msg-row[aria-current='page'] .msg-row-preview, .msg-row[aria-current='page'] .msg-row-time { color: rgba(255,255,255,.85); }
.msg-avatar { width: 44px; height: 44px; border-radius: 50%; display: grid; place-items: center; font-weight: 700;
  color: #fff; background: linear-gradient(160deg, #b9b3a9, #8f897f); font-size: 16px; }
.msg-row-top { display: flex; justify-content: space-between; gap: 8px; align-items: baseline; }
.msg-row-name { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.msg-row-time { font-size: 12px; color: var(--ink-soft); white-space: nowrap; }
.msg-row-preview { font-size: 14px; color: var(--ink-soft); display: -webkit-box; -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; overflow: hidden; line-height: 1.3; }
.msg-row-unread { display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: var(--crave-red);
  margin-right: 6px; vertical-align: middle; }
.msg-row[aria-current='page'] .msg-row-unread { background: #fff; }
.msg-tag { font-size: 11px; font-weight: 700; letter-spacing: .03em; text-transform: uppercase; color: var(--crave-red); }

.msg-pane { display: flex; flex-direction: column; min-height: 0; min-width: 0; }
.msg-empty { margin: auto; text-align: center; color: var(--ink-soft); max-width: 300px; padding: 24px; }
.msg-empty strong { color: var(--ink); font-size: 20px; }
.msg-head { display: flex; align-items: center; gap: 12px; padding: 10px 16px; border-bottom: 1px solid var(--line);
  background: rgba(255,255,255,.92); }
.msg-back { display: none; min-height: 44px; align-items: center; color: var(--crave-red); font-weight: 600;
  text-decoration: none; padding-right: 6px; }
.msg-head-main { flex: 1; min-width: 0; }
.msg-head-name { font-weight: 700; font-size: 17px; }
.msg-head-sub { font-size: 13px; color: var(--ink-soft); display: flex; gap: 10px; flex-wrap: wrap; }
.msg-head-sub a { color: var(--ink); font-weight: 600; min-height: 32px; display: inline-flex; align-items: center; }
.msg-scroll { flex: 1; overflow-y: auto; padding: 16px 16px 8px; display: flex; flex-direction: column; gap: 3px; }
.msg-day { align-self: center; font-size: 12px; color: var(--ink-soft); margin: 14px 0 6px; font-weight: 600; }
.msg-bubble { max-width: min(75%, 520px); padding: 8px 13px; border-radius: 19px; line-height: 1.35;
  white-space: pre-wrap; overflow-wrap: anywhere; font-size: 16px; }
.msg-in { align-self: flex-start; background: #ebe8e2; color: var(--ink); border-bottom-left-radius: 6px; }
.msg-out { align-self: flex-end; background: var(--crave-red); color: #fff; border-bottom-right-radius: 6px; }
.msg-auto { align-self: flex-end; background: #f3f0ea; color: var(--ink); border: 1px dashed #d8d2c8;
  border-bottom-right-radius: 6px; }
.msg-meta { font-size: 11px; color: var(--ink-soft); margin: 1px 6px 8px; }
.msg-meta-out { align-self: flex-end; text-align: right; }
.msg-meta-in { align-self: flex-start; }
.msg-meta[data-failed] { color: var(--crave-red); font-weight: 600; }
.msg-media { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
.msg-media img { max-width: 220px; max-height: 220px; border-radius: 12px; display: block; }
.msg-compose { display: flex; gap: 8px; align-items: flex-end; padding: 10px 12px; border-top: 1px solid var(--line); }
.msg-input { flex: 1; resize: none; min-height: 44px; max-height: 160px; border: 1px solid var(--line); border-radius: 22px;
  padding: 10px 16px; font-size: 16px; line-height: 1.35; color: var(--ink); background: #fff; }
.msg-input:focus-visible { outline: 2px solid var(--crave-red); outline-offset: 0; }
.msg-send { width: 44px; height: 44px; border-radius: 50%; border: 0; background: var(--crave-red); color: #fff;
  display: grid; place-items: center; cursor: pointer; flex: none; }
.msg-send:disabled { background: #d9d4cc; cursor: default; }
.msg-count { font-size: 11px; color: var(--ink-soft); padding: 0 16px 8px; text-align: right; }
.msg-closed { padding: 14px 16px; border-top: 1px solid var(--line); color: var(--ink-soft); font-size: 14px; text-align: center; }

@media (max-width: 760px) {
  .msg-shell { grid-template-columns: 1fr; height: calc(100dvh - 90px); min-height: 460px; border-radius: 12px; }
  .msg-head { padding: 6px 10px; gap: 8px; }
  .msg-head .msg-avatar { display: none; }
  .msg-head-sub { gap: 2px 10px; }
  .msg-shell[data-open='list'] .msg-pane { display: none; }
  .msg-shell[data-open='thread'] .msg-list { display: none; }
  .msg-list { border-right: 0; }
  .msg-back { display: inline-flex; }
  .msg-bubble { max-width: 82%; }
}
`;

function initialsOf(name: string | null | undefined, phone: string) {
  const letters = (name ?? '')
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return letters || phone.replace(/\D/g, '').slice(-2);
}

/** "9:41 AM" today, "Yesterday", a weekday this week, else a date. */
function shortTime(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const days = Math.floor(
    (new Date(now.toDateString()).getTime() - new Date(d.toDateString()).getTime()) / 86_400_000,
  );
  if (days <= 0) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (days === 1) return 'Yesterday';
  if (days < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], { month: 'numeric', day: 'numeric', year: '2-digit' });
}

function ConversationList({ selected, onNew }: { selected: string | null; onNew: () => void }) {
  const [q, setQ] = useState('');
  const live = useInboxLiveState();
  const list = useQuery({
    queryKey: ['messages', q],
    queryFn: async () =>
      (await api.get<{ items: ConversationItem[] }>(`/admin/messages?q=${encodeURIComponent(q)}`))
        .data.items,
    refetchInterval: live === 'live' ? 120_000 : 10_000,
  });
  return (
    <aside className="msg-list" aria-label="Conversations">
      <div className="msg-list-head">
        <div className="msg-list-title">
          <h2>Messages</h2>
          <span className="msg-live" data-state={live} title={LIVE_TITLE[live]}>
            {live === 'live' ? 'Live' : live === 'off' ? '' : 'Connecting…'}
          </span>
          <Button
            variant="ghost"
            onClick={onNew}
            aria-label="New message"
            style={{ padding: '0 10px' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M4 20h4L19 9l-4-4L4 16v4Zm11-15 4 4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </div>
        <input
          className="msg-search"
          type="search"
          placeholder="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search conversations"
        />
      </div>
      <div className="msg-rows">
        {list.isError && (
          <Notice kind="error">
            {list.error instanceof ApiError ? list.error.message : 'Could not load messages.'}
          </Notice>
        )}
        {list.data?.length === 0 && (
          <p style={{ color: 'var(--ink-soft)', padding: '8px 10px', margin: 0 }}>
            {q ? 'No conversations match.' : 'No texts yet.'}
          </p>
        )}
        {list.data?.map((v) => (
          <Link
            key={v.id}
            to={`/messages/${v.id}`}
            className="msg-row"
            aria-current={v.id === selected ? 'page' : undefined}
          >
            <span className="msg-avatar" aria-hidden="true">
              {initialsOf(v.customer?.name, v.phone)}
            </span>
            <span style={{ minWidth: 0 }}>
              <span className="msg-row-top">
                <span className="msg-row-name">
                  {v.unread > 0 && <span className="msg-row-unread" aria-label="Unread" />}
                  {v.customer?.name ?? v.phone_display}
                </span>
                <span className="msg-row-time">{shortTime(v.last_message_at)}</span>
              </span>
              <span className="msg-row-preview">
                {v.opted_out && <span className="msg-tag">Stopped · </span>}
                {v.last_message
                  ? `${v.last_message.direction === 'outbound' ? 'You: ' : ''}${v.last_message.body || 'Photo'}`
                  : ''}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </aside>
  );
}

/** GSM texts are 160 characters (153 each when split); anything else is 70 (67 when split). */
function segments(text: string) {
  if (!text) return 0;
  const gsm = /^[\n\r\x20-\x7e£¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ¡ÄÖÑÜ§¿äöñüà€^{}\\[~\]|]*$/.test(text);
  const [one, many] = gsm ? [160, 153] : [70, 67];
  return text.length <= one ? 1 : Math.ceil(text.length / many);
}

function Composer({
  disabled,
  pending,
  onSend,
}: {
  disabled?: boolean;
  pending: boolean;
  onSend: (text: string) => Promise<boolean>;
}) {
  const [text, setText] = useState('');
  const box = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, 160)}px`;
  }, [text]);
  async function submit(e?: FormEvent) {
    e?.preventDefault();
    const t = text.trim();
    if (!t || pending) return;
    // Like Messages: the box clears at once and the text shows in the thread while it sends. If it
    // could not be sent at all, the words come back so nothing is lost.
    setText('');
    if (!(await onSend(t))) setText((now) => now || t);
  }
  const count = segments(text.trim());
  return (
    <>
      <form className="msg-compose" onSubmit={(e) => void submit(e)}>
        <textarea
          ref={box}
          className="msg-input"
          rows={1}
          placeholder="Text message"
          aria-label="Text message"
          maxLength={1000}
          value={text}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
            // Return sends on a computer; Shift+Return adds a line. A phone keyboard adds a line.
            if (e.key === 'Enter' && !e.shiftKey && window.matchMedia('(pointer: fine)').matches) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        <button
          type="submit"
          className="msg-send"
          disabled={disabled || pending || !text.trim()}
          aria-label="Send"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 19V5m0 0-6 6m6-6 6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>
      {count > 1 && <div className="msg-count">Sends as {count} texts</div>}
    </>
  );
}

const LIVE_TITLE: Record<string, string> = {
  live: 'New texts and delivery updates appear as they happen.',
  connecting: 'Connecting for live updates. Until then, this checks every few seconds.',
  retrying: 'Reconnecting for live updates. Until then, this checks every few seconds.',
  off: '',
};

const STATUS_LABEL: Record<string, string> = {
  queued: 'Sending…',
  sent: 'Sent',
  delivered: 'Delivered',
  undelivered: 'Not delivered',
  failed: 'Not sent',
};

function Thread({ id }: { id: string }) {
  const qc = useQueryClient();
  const live = useInboxLiveState();
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const convo = useQuery({
    queryKey: ['message-thread', id],
    queryFn: async () => (await api.get<Conversation>(`/admin/messages/${id}`)).data,
    refetchInterval: live === 'live' ? 120_000 : 5_000,
  });
  const scroller = useRef<HTMLDivElement>(null);
  const count = convo.data?.messages.length ?? 0;
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count]);
  useEffect(() => {
    void qc.invalidateQueries({ queryKey: ['messages-unread'] });
    void qc.invalidateQueries({ queryKey: ['messages'] });
  }, [qc, id, count]);

  const send = useMutation({
    mutationFn: async (text: string) =>
      (await api.post<SendResult>(`/admin/messages/${id}/messages`, { body: text })).data,
  });

  if (convo.isError)
    return (
      <div style={{ padding: 16 }}>
        <Notice kind="error">
          {convo.error instanceof ApiError
            ? convo.error.message
            : 'Could not open this conversation.'}
        </Notice>
      </div>
    );
  const v = convo.data;
  if (!v) return <p style={{ padding: 16 }}>Loading…</p>;
  const name = v.customer?.name ?? v.phone_display;
  const lastOut = [...v.messages].reverse().find((m) => m.direction === 'outbound');

  let day = '';
  return (
    <>
      <header className="msg-head">
        <Link to="/messages" className="msg-back" aria-label="Back to Messages">
          ‹ Back
        </Link>
        <span className="msg-avatar" aria-hidden="true">
          {initialsOf(v.customer?.name, v.phone)}
        </span>
        <div className="msg-head-main">
          <div className="msg-head-name">{name}</div>
          <div className="msg-head-sub">
            {v.customer?.name && <span>{v.phone_display}</span>}
            {v.customer && <Link to={`/customers/${v.customer.id}`}>Customer</Link>}
            {v.latest_order && (
              <Link to={`/orders/${v.latest_order.id}`}>
                {v.latest_order.order_number} · {statusWords(v.latest_order.status)}
              </Link>
            )}
          </div>
        </div>
      </header>
      <div className="msg-scroll" ref={scroller} aria-live="polite">
        {v.messages.map((m) => {
          const d = new Date(m.created_at);
          const label = d.toLocaleDateString([], {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
          });
          const showDay = label !== day;
          day = label;
          const out = m.direction === 'outbound';
          // Photos arrive as a list; anything else (an early row saved as text) shows no photos.
          const media = Array.isArray(m.media) ? m.media.filter((u) => typeof u === 'string') : [];
          const failed = m.status === 'failed' || m.status === 'undelivered';
          const meta = out
            ? [
                m.source === 'automatic'
                  ? `Automatic${m.order_number ? ` · ${m.order_number}` : ''}`
                  : m.sent_by_name,
                d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
                failed || m === lastOut ? (STATUS_LABEL[m.status] ?? m.status) : null,
              ]
                .filter(Boolean)
                .join(' · ')
            : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
          return (
            <div key={m.id} style={{ display: 'contents' }}>
              {showDay && <div className="msg-day">{label}</div>}
              <div
                className={`msg-bubble ${out ? (m.source === 'automatic' ? 'msg-auto' : 'msg-out') : 'msg-in'}`}
                data-pending={m.id.startsWith('pending-') ? '' : undefined}
              >
                {m.body}
                {media.length > 0 && (
                  <span className="msg-media">
                    {media.map((url) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer">
                        <img src={url} alt="Photo from the customer" loading="lazy" />
                      </a>
                    ))}
                  </span>
                )}
              </div>
              <div
                className={`msg-meta ${out ? 'msg-meta-out' : 'msg-meta-in'}`}
                data-failed={failed ? '' : undefined}
                title={m.error ?? undefined}
              >
                {meta}
                {failed && m.error ? `: ${m.error}` : ''}
              </div>
            </div>
          );
        })}
      </div>
      {error && (
        <div style={{ padding: '0 12px' }}>
          <Notice kind="error">{error}</Notice>
        </div>
      )}
      {v.opted_out ? (
        <div className="msg-closed">
          This number texted STOP, so it can’t be texted. It opens again if they text START.
        </div>
      ) : (
        <Composer
          pending={send.isPending}
          onSend={async (text) => {
            setError(null);
            try {
              const pendingId = `pending-${Date.now()}`;
              qc.setQueryData<Conversation>(['message-thread', id], (old) =>
                old
                  ? {
                      ...old,
                      messages: [
                        ...old.messages,
                        {
                          id: pendingId,
                          direction: 'outbound',
                          source: 'staff',
                          body: text,
                          media: [],
                          status: 'queued',
                          error: null,
                          created_at: new Date().toISOString(),
                          sent_by_name: null,
                          order_number: null,
                        },
                      ],
                    }
                  : old,
              );
              let r: SendResult;
              try {
                r = await send.mutateAsync(text);
              } finally {
                await qc.invalidateQueries({ queryKey: ['message-thread', id] });
              }
              if (r.status === 'failed') setError(`Not sent: ${r.error ?? 'Twilio refused it.'}`);
              return true;
            } catch (e) {
              setError(e instanceof ApiError ? e.message : 'The text could not be sent.');
              if (e instanceof ApiError && e.status === 404) void nav('/messages');
              return false;
            }
          }}
        />
      )}
    </>
  );
}

function NewText({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const [to, setTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: async (text: string) =>
      (await api.post<SendResult>('/admin/messages', { to, body: text })).data,
  });
  return (
    <>
      <header className="msg-head">
        <button
          type="button"
          className="msg-back"
          onClick={onClose}
          style={{ background: 'none', border: 0, font: 'inherit' }}
        >
          ‹ Messages
        </button>
        <label className="msg-head-main" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--ink-soft)' }}>To:</span>
          <input
            type="tel"
            inputMode="tel"
            autoFocus
            placeholder="(863) 555-0100"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={{
              flex: 1,
              minHeight: 44,
              border: 0,
              fontSize: 17,
              background: 'transparent',
              color: 'var(--ink)',
            }}
          />
        </label>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </header>
      <div className="msg-scroll">
        <p style={{ color: 'var(--ink-soft)', margin: 'auto', textAlign: 'center', maxWidth: 320 }}>
          The text comes from (863) 777-4887. Only text people who expect to hear from Crave, like a
          customer about their order.
        </p>
      </div>
      {error && (
        <div style={{ padding: '0 12px' }}>
          <Notice kind="error">{error}</Notice>
        </div>
      )}
      <Composer
        pending={send.isPending}
        onSend={async (text) => {
          setError(null);
          try {
            const r = await send.mutateAsync(text);
            if (r.status === 'failed') setError(`Not sent: ${r.error ?? 'Twilio refused it.'}`);
            onClose();
            void nav(`/messages/${r.conversationId}`);
            return true;
          } catch (e) {
            setError(e instanceof ApiError ? e.message : 'The text could not be sent.');
            return false;
          }
        }}
      />
    </>
  );
}
